import { LAND_PATHS } from "./land-paths";
import { placeId } from "./places";
import type { Person } from "./share";
import { formatClock, shortName } from "./time";

export const MAP_W = 1000;
export const MAP_H = 480;
const LAT_MAX = 84;
const LAT_MIN = -56;

export function project(lng: number, lat: number): [number, number] {
  const x = ((lng + 180) / 360) * MAP_W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * MAP_H;
  return [x, y];
}

function toCartesian(lng: number, lat: number): [number, number, number] {
  const λ = (lng * Math.PI) / 180;
  const φ = (lat * Math.PI) / 180;
  return [Math.cos(φ) * Math.cos(λ), Math.cos(φ) * Math.sin(λ), Math.sin(φ)];
}

function fromCartesian(x: number, y: number, z: number): [number, number] {
  const hyp = Math.hypot(x, y);
  return [(Math.atan2(y, x) * 180) / Math.PI, (Math.atan2(z, hyp) * 180) / Math.PI];
}

function slerp(
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] {
  const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const θ = Math.acos(dot);
  if (θ < 1e-6) return a;
  const s = Math.sin(θ);
  const w1 = Math.sin((1 - t) * θ) / s;
  const w2 = Math.sin(t * θ) / s;
  return [a[0] * w1 + b[0] * w2, a[1] * w1 + b[1] * w2, a[2] * w1 + b[2] * w2];
}

export function greatCirclePath(from: Person, to: Person): string {
  const a = toCartesian(from.lng, from.lat);
  const b = toCartesian(to.lng, to.lat);
  const steps = 48;
  const chunks: string[] = [];
  let current: string[] = [];
  let prevX: number | null = null;
  for (let i = 0; i <= steps; i++) {
    const [lng, lat] = fromCartesian(...slerp(a, b, i / steps));
    const [x, y] = project(lng, lat);
    if (prevX !== null && Math.abs(x - prevX) > MAP_W * 0.5) {
      if (current.length) chunks.push(current.join(" "));
      current = [`M${x.toFixed(1)},${y.toFixed(1)}`];
    } else {
      current.push(`${current.length ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`);
    }
    prevX = x;
  }
  if (current.length) chunks.push(current.join(" "));
  return chunks.join("");
}

function fitViewBox(people: Person[]): { x: number; y: number; w: number; h: number } {
  if (!people.length) return { x: 0, y: 0, w: MAP_W, h: MAP_H };
  const pts = people.map((p) => project(p.lng, p.lat));
  let minX = Math.min(...pts.map((p) => p[0]));
  let maxX = Math.max(...pts.map((p) => p[0]));
  let minY = Math.min(...pts.map((p) => p[1]));
  let maxY = Math.max(...pts.map((p) => p[1]));
  if (maxX - minX > MAP_W * 0.62) {
    minX = 0;
    maxX = MAP_W;
  }
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  const padX = Math.max(110, spanX * 0.28);
  const padY = Math.max(70, spanY * 0.32);
  const w = Math.min(MAP_W + 80, Math.max(spanX + padX * 2, 420));
  const h = Math.min(MAP_H + 40, Math.max(spanY + padY * 2, 210));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const x = Math.max(-12, Math.min(cx - w / 2, MAP_W - w + 12));
  const y = Math.max(-12, Math.min(cy - h / 2, MAP_H - h + 12));
  return { x, y, w, h };
}

function solarDeclination(when: Date): number {
  const start = Date.UTC(when.getUTCFullYear(), 0, 0);
  const doy = (when.getTime() - start) / 86_400_000;
  return 23.44 * Math.sin(((2 * Math.PI) / 365) * (doy - 81));
}

function subsolarLng(when: Date): number {
  const hours = when.getUTCHours() + when.getUTCMinutes() / 60 + when.getUTCSeconds() / 3600;
  return 15 * (12 - hours);
}

function terminatorLat(lng: number, decDeg: number, sunLng: number): number {
  const hourAngle = ((lng - sunLng) * Math.PI) / 180;
  const dec = (decDeg * Math.PI) / 180;
  if (Math.abs(dec) < 0.004) return 0;
  return (Math.atan(-Math.cos(hourAngle) / Math.tan(dec)) * 180) / Math.PI;
}

function nightPaths(when: Date): { fill: string; line: string } {
  const dec = solarDeclination(when);
  const sunLng = ((subsolarLng(when) + 540) % 360) - 180;
  const nightSouth = dec >= 0;
  const pts: [number, number][] = [];
  for (let lng = -180; lng <= 180; lng += 3) {
    const lat = Math.max(LAT_MIN, Math.min(LAT_MAX, terminatorLat(lng, dec, sunLng)));
    pts.push(project(lng, lat));
  }
  const poleY = project(0, nightSouth ? LAT_MIN : LAT_MAX)[1];
  const left = project(-180, 0)[0];
  const right = project(180, 0)[0];
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join("");
  const fill = `${line}L${right.toFixed(1)},${poleY.toFixed(1)}L${left.toFixed(1)},${poleY.toFixed(1)}Z`;
  return { fill, line };
}

export function mapHtml(people: Person[], activeId: string | null, when: Date): string {
  const land = LAND_PATHS.map((d) => `<path class="map-land" d="${d}"></path>`).join("");
  const arcs: string[] = [];
  const defs: string[] = [];
  people.forEach((a, i) => {
    people.slice(i + 1).forEach((b, j) => {
      const id = `arc-${i}-${i + 1 + j}`;
      const d = greatCirclePath(a, b);
      const involved = placeId(a) === activeId || placeId(b) === activeId;
      defs.push(`
        <linearGradient id="${id}" gradientUnits="userSpaceOnUse"
          x1="${project(a.lng, a.lat)[0].toFixed(1)}" y1="${project(a.lng, a.lat)[1].toFixed(1)}"
          x2="${project(b.lng, b.lat)[0].toFixed(1)}" y2="${project(b.lng, b.lat)[1].toFixed(1)}">
          <stop offset="0%" stop-color="${a.color}"></stop>
          <stop offset="100%" stop-color="${b.color}"></stop>
        </linearGradient>
      `);
      arcs.push(
        `<path d="${d}" fill="none" stroke="url(#${id})" stroke-width="${involved ? 2.4 : 1.8}"
          stroke-linecap="round" vector-effect="non-scaling-stroke" opacity="${involved ? 0.95 : 0.75}"></path>`,
      );
    });
  });

  const pins = people
    .map((person) => {
      const [x, y] = project(person.lng, person.lat);
      const id = placeId(person);
      const active = id === activeId;
      const label = `${person.city}  ·  ${shortName(when, person.iana)}  ·  ${formatClock(when, person.iana)}`;
      const tooltip = active
        ? `<g class="pin-label" pointer-events="none">
            <rect class="pin-label-bg" x="-86" y="-34" width="172" height="22" rx="6"></rect>
            <text class="pin-label-text" y="-19" text-anchor="middle">${escapeHtml(label)}</text>
          </g>`
        : "";
      return `
        <g class="pin ${active ? "is-active" : ""}" data-id="${escapeHtml(id)}"
          transform="translate(${x.toFixed(1)} ${y.toFixed(1)})" aria-label="${escapeHtml(person.city)}">
          <circle class="pin-halo" r="11" fill="${person.color}"></circle>
          <circle class="pin-dot" r="6" fill="${person.color}" stroke="#fff" stroke-width="2"></circle>
          ${tooltip}
        </g>
      `;
    })
    .join("");

  const vb = fitViewBox(people);
  const night = nightPaths(when);
  const host = people[0];
  const caption = host
    ? `Day / night at ${formatClock(when, host.iana)} ${host.city}`
    : "Day / night";

  return `
    <svg class="map-svg" viewBox="${vb.x.toFixed(1)} ${vb.y.toFixed(1)} ${vb.w.toFixed(1)} ${vb.h.toFixed(1)}" preserveAspectRatio="xMidYMid meet" role="img">
      <defs>${defs.join("")}</defs>
      ${land}
      <path class="map-night" d="${night.fill}"></path>
      <path class="map-terminator" d="${night.line}"></path>
      ${arcs.join("")}
      ${pins}
    </svg>
    <div class="map-dn-caption">${escapeHtml(caption)}</div>
  `;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => {
    switch (ch) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}
